import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from './api';
import HamburgerMenu from './HamburgerMenu';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

// Register the necessary components for Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        background: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#212529',
        border: isDark ? '#444' : '#dee2e6',
        link: isDark ? '#6cb2f7' : '#007bff',
        menuItemColor: isDark ? '#e0e0e0' : '#333',
    };

    return {
        container: { width: '90%', maxWidth: '1000px', margin: '20px auto', fontFamily: 'sans-serif', backgroundColor: colors.background, color: colors.text },
        header: { textAlign: 'center', marginBottom: '20px' },
        pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
        pageTitle: { fontSize: '24px', margin: 0 },
        menuItem: {
            padding: '10px 15px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'block',
            textDecoration: 'none',
            color: colors.menuItemColor,
        },
        menuItemButton: {
            background: 'none',
            border: 'none',
            padding: '10px 15px',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '16px',
            fontFamily: 'sans-serif',
            color: colors.menuItemColor,
        },
        chartContainer: {
            padding: '20px',
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            backgroundColor: colors.background
        }
    };
};

function BurndownChartPage({ theme }) {
    const styles = getStyles(theme);
    const [chartData, setChartData] = useState(null);
    const [inspectionInfo, setInspectionInfo] = useState(null);
    const { inspectionId } = useParams();

    useEffect(() => {
        if (inspectionId) {
            // Fetch both the chart data and the main inspection info
            Promise.all([
                api.get(`/inspections/${inspectionId}/`),
                api.get(`/inspections/${inspectionId}/burndown-chart/`)
            ]).then(([inspectionRes, chartRes]) => {
                setInspectionInfo(inspectionRes.data);
                const data = chartRes.data;

                // --- Process data for the chart ---
                const labels = [];
                const idealData = [];
                const actualData = [];

                const startDate = new Date(data.start_date);
                const ecdDate = new Date(data.ecd);
                const totalTasks = data.total_items;

                // Create labels for each weekday from start to ECD (skip weekends)
                let currentDate = new Date(startDate);
                while (currentDate <= ecdDate) {
                    const dayOfWeek = currentDate.getDay();
                    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                        labels.push(currentDate.toLocaleDateString());
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                }

                // Calculate the ideal burndown line
                const totalDays = labels.length - 1;
                for (let i = 0; i <= totalDays; i++) {
                    const idealTasksRemaining = totalTasks - (totalTasks / totalDays) * i;
                    idealData.push(Math.round(idealTasksRemaining));
                }
                
                // Map the actual progress data
                const dailyProgress = Array.isArray(data.daily_progress) ? data.daily_progress : [];
                const actualProgressMap = new Map(dailyProgress.map(p => [new Date(p.date).toLocaleDateString(), p.tasks_remaining]));

                // Create the actual data line, filling in gaps
                let lastKnownValue = totalTasks;
                for (const label of labels) {
                    if (actualProgressMap.has(label)) {
                        lastKnownValue = actualProgressMap.get(label);
                    }
                    if (new Date(label) > new Date()) {
                        // Don't plot future data
                        actualData.push(null);
                    } else {
                        actualData.push(lastKnownValue);
                    }
                }

                setChartData({
                    labels,
                    datasets: [
                        {
                            label: 'Ideal Burndown',
                            data: idealData,
                            borderColor: 'rgb(255, 99, 132)',
                            backgroundColor: 'rgba(255, 99, 132, 0.5)',
                            stepped: true,
                        },
                        {
                            label: 'Actual Burndown',
                            data: actualData,
                            borderColor: 'rgb(53, 162, 235)',
                            backgroundColor: 'rgba(53, 162, 235, 0.5)',
                        },
                    ],
                });

            }).catch(err => {
                console.error("Error fetching chart data:", err);
            });
        }
    }, [inspectionId]);

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Inspection Burndown Chart',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Tasks Remaining'
                }
            }
        }
    };

    if (!inspectionInfo) {
        return <div>Loading Chart Data...</div>;
    }


    return (
        <div style={styles.container}>
            <div style={styles.pageHeader}>
                <HamburgerMenu theme={theme}/>
                <h1 style={styles.pageTitle}>Burndown Chart</h1>
                <div style={{ width: '45px' }}></div>
            </div>
            <div style={styles.header}>
                <h3>BUNO: {inspectionInfo.buno} ({inspectionInfo.phase})</h3>
            </div>
            <div style={styles.chartContainer}>
                {chartData ? <Line options={chartOptions} data={chartData} /> : <p>Generating chart...</p>}
            </div>
        </div>
    );
}

export default BurndownChartPage;
